import { SearchOutlined } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import { Box, Button, Card, IconButton, TextField, styled } from "@mui/material"
import { forwardRef, useContext, useLayoutEffect, useRef, useState } from "react"
import type { KeyboardEvent } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"

import { sendSearchEvent } from "@/utils/posthog"

import { MainLayoutContext } from "."

export const SearchOutlinedIcon = styled(SearchOutlined)(({ theme }) => ({
  color: theme.palette.grey[600],
  marginRight: 6,
}))

export const SearchResultCard = styled(Card)({
  zIndex: 99,
  top: "100%",
  width: "100%",
  position: "absolute",
  paddingTop: "0.5rem",
  paddingBottom: "0.5rem",
})

const SearchInput = forwardRef(function SearchInput(props, ref) {
  const parentRef = useRef()
  const { t } = useTranslation("common")
  const { setShowMobileSearch } = useContext(MainLayoutContext)
  const navigate = useNavigate()
  const [search, setSearch] = useState("")

  const location = useLocation()

  useLayoutEffect(() => {
    if (location.pathname !== "/") {
      setSearch("")
    }
  }, [location.pathname])

  useLayoutEffect(() => {
    if (location.pathname === "/") {
      const searchParams = new URLSearchParams(location.search)
      setSearch(searchParams.get("query") || "")
    }
  }, [location.pathname, location.search])

  const handleSearch = () => {
    setShowMobileSearch(false)
    sendSearchEvent(search)
    navigate({
      pathname: "/",
      ...(search && { search: `?query=${encodeURIComponent(search.trim())}` }),
    })
    window.scrollTo({ top: 0 })
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      ;(e.target as HTMLInputElement).blur()
      handleSearch()
    }
  }

  return (
    <Box
      ref={parentRef}
      sx={{
        position: "relative",
        flex: "1 1 0",
        maxWidth: "670px",
        mx: "auto",
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        placeholder={t("Searchingfor") + "..."}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={handleKeyDown}
        value={search}
        inputRef={ref}
        slotProps={{
          input: {
            onKeyPress: handleKeyDown,
            autoComplete: "off",
            spellCheck: "false",
            sx: {
              height: 44,
              paddingRight: 0,
              borderRadius: 300,
              color: "grey.700",
              overflow: "hidden",
              backgroundColor: "background.paper",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.400",
              },
            },
            endAdornment: (
              <>
                <IconButton
                  onClick={() => {
                    setSearch("")
                    if (location.pathname === "/") {
                      navigate("/")
                    }
                  }}
                  disabled={!search}
                  sx={{
                    height: "80%",
                    borderRadius: "300px",
                    color: "grey.700",
                    opacity: search ? 1 : 0,
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <Button
                  color="inherit"
                  variant="contained"
                  sx={{
                    px: "2rem",
                    height: "100%",
                    borderRadius: "0 300px 300px 0",
                    marginLeft: "1px",
                  }}
                  onClick={handleSearch}
                >
                  {t("Search")}
                </Button>
              </>
            ),
            startAdornment: <SearchOutlinedIcon fontSize="small" />,
          },
        }}
      />
    </Box>
  )
})
export default SearchInput
